import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/AuthShell";
import { PasswordInput } from "@/components/PasswordInput";
import { api, errMsg } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const isInvite = params.get("invitasjon") === "1";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [role, setRole] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Passordet må ha minst 8 tegn.");
    if (pw !== pw2) return setErr("Passordene er ikke like.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      setRole(data.role);
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <h2 className="font-display text-3xl font-semibold tracking-tight" data-testid="reset-title">{isInvite ? "Velkommen! Lag ditt passord" : "Velg nytt passord"}</h2>
      {isInvite && !role && <p className="mt-2 text-sm text-slate-600">Du er invitert til havnekalkulatoren. Velg et passord du skal bruke når du logger inn.</p>}
      {role ? (
        <div className="mt-8 space-y-5" data-testid="reset-success">
          <div className="flex gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            <CheckCircle2 className="w-5 h-5 shrink-0" /> {isInvite ? "Passordet er laget. Du kan nå logge inn." : "Passordet er oppdatert. Du kan nå logge inn."}
          </div>
          <Button asChild className="w-full h-11">
            <Link to={role === "super_admin" ? "/superadmin/login" : "/login"} data-testid="reset-go-login">Gå til innlogging</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5">
          {!token && <p className="text-sm text-red-700">Lenken mangler token. Be om en ny lenke.</p>}
          <div className="space-y-2">
            <Label>{isInvite ? "Passord (minst 8 tegn)" : "Nytt passord"}</Label>
            <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" testId="reset-password-input" />
          </div>
          <div className="space-y-2">
            <Label>Gjenta passord</Label>
            <PasswordInput value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" testId="reset-password-confirm-input" />
          </div>
          {err && <p className="text-sm text-red-700" data-testid="reset-error">{err}</p>}
          <Button type="submit" disabled={busy || !token} className="w-full h-11" data-testid="reset-submit-button">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isInvite ? "Lagre passord" : "Lagre nytt passord"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
